import { Link } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { AppChrome } from '@/components/AppChrome';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { useFiling, useFilingActions } from './state';

export function ReviewPage() {
  const access = useSurfaceAccess(SURFACES['return-review']);
  const filingId = useFiling((snapshot) => snapshot.context.filingId);
  const error = useFiling((snapshot) => snapshot.context.error);
  const requesting = useFiling((snapshot) => snapshot.matches({ workflow: 'requesting' }));
  const actions = useFilingActions();
  if (access === 'waiting') return <p className="p-8 text-sm text-muted-foreground">Checking screen access…</p>;
  if (access === 'unavailable')
    return <p className="p-8 text-sm text-destructive">This screen is unavailable right now.</p>;
  if (access === 'denied')
    return <p className="p-8 text-sm text-muted-foreground">You have no access to this screen.</p>;
  if (!filingId)
    return (
      <AppChrome title="Return review" description="Review follows Filing setup.">
        <Card>
          <CardContent className="space-y-3 p-6">
            <p className="font-medium">No Filing is open.</p>
            <Button render={<Link to="/filing-setup" />}>Go to Filing setup</Button>
          </CardContent>
        </Card>
      </AppChrome>
    );
  return (
    <AppChrome
      title="Return review"
      description="Request deterministic calculation, then review the return lines and evidence."
    >
      <div className="space-y-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Calculation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Calculation is for the selected Filing.</p>
            <p className="text-sm text-muted-foreground">
              The Filing review read is not available in this workspace yet. You can still request the server-side
              calculation for this selected Filing.
            </p>
            {error && (
              <p className="text-sm text-destructive">The calculation could not be requested. Please try again.</p>
            )}
            <Button data-jr-key="request-calculation" onClick={() => actions.request(filingId)} disabled={requesting}>
              {requesting ? 'Requesting…' : 'Request calculation'}
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex gap-3 p-6">
            <CheckCircle2 className="mt-0.5 size-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Line review and IRS rule interpretation are unavailable until their integrations are connected.
            </p>
          </CardContent>
        </Card>
      </div>
    </AppChrome>
  );
}
